import { RefreshCw, Eye, CreditCard, Zap, UserCog, ArrowDownLeft } from 'lucide-react';

interface WalletChargeCategory {
  category: 'manual_topup' | 'auto_topup' | 'admin_adjustments' | 'transfer_received';
  displayName: string;
  count: number;
  total_amount: number;
}

interface WalletChargeSummaryProps {
  categories: WalletChargeCategory[];
  total: number;
  loading?: boolean;
  error?: string | null;
  onViewDetails?: (category: string) => void;
}

// Category display configuration
const CATEGORY_CONFIG: Record<string, { label: string; icon: React.ReactNode }> = {
  manual_topup: { label: 'Manual Wallet Top-up', icon: <CreditCard className="w-5 h-5" /> },
  auto_topup: { label: 'Auto Wallet Top-up', icon: <Zap className="w-5 h-5" /> },
  admin_adjustments: { label: 'Admin Adjustments', icon: <UserCog className="w-5 h-5" /> },
  transfer_received: { label: 'Transfer Received', icon: <ArrowDownLeft className="w-5 h-5" /> },
};

/**
 * Wallet Charge Summary table by category (Level 1)
 * Shows wallet top-ups and admin adjustments grouped by category
 */
export function WalletChargeSummary({ categories, total, loading, error, onViewDetails }: WalletChargeSummaryProps) {
  const formatCurrency = (amount: number) => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(Math.abs(amount));
    return amount >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  const formatCount = (count: number) => {
    return `${count.toLocaleString()} transaction${count !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Wallet Charge Summary</h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Wallet Charge Summary</h2>
        </div>
        <div className="p-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // If no categories have any transactions, show empty state
  if (categories.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Wallet Charge Summary</h2>
        </div>
        <div className="p-8 text-center text-gray-500 text-sm">
          No wallet charges for this period
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900">Wallet Charge Summary</h2>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Category</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Count</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Total Amount</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {categories.map((cat) => {
              const config = CATEGORY_CONFIG[cat.category] || { label: cat.displayName, icon: null };
              const isNegative = cat.total_amount < 0;
              return (
                <tr key={cat.category} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">{config.icon}</span>
                      <span className="text-sm text-gray-900">{config.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">
                    {formatCount(cat.count)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <span className={`font-medium ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(cat.total_amount)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => onViewDetails?.(cat.category)}
                      className="inline-flex items-center justify-center w-9 h-9 border-2 border-blue-500 text-blue-500 rounded hover:bg-blue-500 hover:text-white transition-colors"
                      title="View details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {/* Totals Row */}
            <tr className="bg-gray-50 border-t-2 border-gray-200">
              <td className="px-4 py-3 text-sm font-bold text-gray-900">Total Wallet Charges</td>
              <td className="px-4 py-3 text-sm text-right text-gray-400">-</td>
              <td className="px-4 py-3 text-sm text-right font-bold text-green-600">
                {formatCurrency(total)}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
